import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useContext, useEffect, useRef } from "react";
import useGetOrg from "src/hooks/useGetOrganization";
import { getConnectionsHistoryRequest } from "src/services/organizations/queries/count";
import { ConnectionCount } from "src/types/apps";

import { useGetApp } from "../../AppSettings/Details/hook/useGetApp";
import { StatusRequest } from "../ConnectionsPeriodChart/store";
import { PeriodsType } from "../ConnectionsPeriodChart/types";
import Context from "../context";

// ----------------------------------------------------------------------

type Available = { [key: string]: boolean };

type Props = {
	actions: {
		onSelectPeriod: (val: PeriodsType) => void;
		onSetStatus: (val: StatusRequest) => void;
	};
};

export const useGetAppConnectionsHistory = ({ actions }: Props) => {
	const { product } = useContext(Context);

	const available = useRef<Available>({});
	const queryClient = useQueryClient();

	const org = useGetOrg();
	const app = useGetApp();

	useEffect(() => {
		getConnectionsHistoryRequest(org.id, app?.id ?? "", "ALL", product).then((data) =>
			onSuccess(data, "ALL", true)
		);
		getConnectionsHistoryRequest(org.id, app?.id ?? "", "7D", product).then((data) =>
			onSuccess(data, "7D")
		);
	}, [org.id, app?.id, product]);

	const onSuccess = (data: ConnectionCount[], period: PeriodsType, isAll?: boolean) => {
		if (!isAll) available.current[period] = true;
		actions.onSetStatus(StatusRequest.onSuccess);

		const newData = [...data];

		const minDate = period === "7D" ? moment().add(-7, "days") : moment(data[0]?.date);
		const maxDate = moment();

		for (let date = minDate.clone(); date.isSameOrBefore(maxDate); date.add(1, "days")) {
			const currentDate = date.format("YYYY-MM-DD"); // Formateamos la fecha a 'YYYY-MM-DD'

			if (!data.some((d) => d?.date === currentDate)) {
				newData.push({
					app: data[0]?.app ?? "",
					count: 0,
					entity: "",
					status: "",
					date: currentDate
				});
			}
		}

		newData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

		queryClient.setQueryData(
			[app?.id, product ? `${product}History` : "allHistory", period],
			newData
		);
		if (!isAll) actions.onSelectPeriod(period);
	};

	const fetchHistoryData = (period: PeriodsType) => {
		actions.onSetStatus(StatusRequest.onFetch);

		const onError = () => actions.onSetStatus(StatusRequest.onError);

		getConnectionsHistoryRequest(org.id, app?.id ?? "", period, product)
			.then((data) => onSuccess(data, period))
			.catch(onError);
	};

	function setPeriod(period: PeriodsType) {
		if (!available.current[period]) fetchHistoryData(period);
		actions.onSelectPeriod(period);
	}

	return { setPeriod };
};

export default useGetAppConnectionsHistory;
